import { z } from "zod";

export const ValidateZSLParamsSchema = z.object({}).passthrough();

export type ValidateZSLParamsType = z.infer<typeof ValidateZSLParamsSchema>;

export const validateZSLExecute = async (rawArgs: any) => {
  let zslToValidate: ValidateZSLParamsType;
  try {
    zslToValidate = ValidateZSLParamsSchema.parse(rawArgs || {});
  } catch (error) {
    console.error(
      "[VALIDATE_ZSL_TOOL] Error parsing ZSL input (expected an object):",
      error,
    );
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid ZSL input. Expected an object. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const proxyApiUrl = "/api/canvasui/composer/validateZsl";

  try {
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(zslToValidate),
    });

    if (!res.ok) {
      let errorDetailString = `Proxy error for validate_zsl (${res.status}).`;
      let responseDataForLog: any =
        "[Could not read error body after initial attempt]";
      try {
        const errorData = await res.json();
        responseDataForLog = errorData;
        if (errorData && errorData.error) {
          errorDetailString = `Proxy error for validate_zsl (${res.status}): ${errorData.error}${errorData.details ? ": " + JSON.stringify(errorData.details) : ""}`;
        } else {
          if (
            typeof errorData.valid === "boolean" &&
            !errorData.valid &&
            Array.isArray(errorData.errors)
          ) {
            errorDetailString = `ZSL Validation Failed (${res.status}): ${errorData.errors.map((e: any) => e.detail || e.message || JSON.stringify(e)).join(", ")}`;
          } else {
            errorDetailString = `Proxy error for validate_zsl (${res.status}): Received non-standard JSON error from proxy: ${JSON.stringify(errorData)}`;
          }
        }
      } catch (e) {
        errorDetailString = `Proxy error for validate_zsl (${res.status}): Proxy error response was not valid JSON.`;
      }
      console.error(
        `[VALIDATE_ZSL_TOOL] Proxy error (${res.status}) for URL ${proxyApiUrl}. Details: ${JSON.stringify(responseDataForLog)}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      let resultText: string;
      if (data.valid) {
        resultText = "ZSL Validation Successful: The ZSL is valid.";
      } else if (data.errors && Array.isArray(data.errors)) {
        resultText =
          "ZSL Validation Failed:\\n" +
          data.errors
            .map(
              (e: any) =>
                `- ${e.title || e.code || "Error"}: ${e.detail || e.message} (Path: ${e.source?.pointer || "N/A"})`,
            )
            .join("\\n");
      } else if (data.message) {
        resultText = data.message;
      } else {
        resultText =
          "ZSL Validation returned an unexpected format: " +
          JSON.stringify(data, null, 2);
      }
      return {
        content: [{ type: "text", text: resultText }],
      };
    } catch (e) {
      console.error(
        `[VALIDATE_ZSL_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
        e,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) for ZSL validation, but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[VALIDATE_ZSL_TOOL] Error in validateZSLExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error during ZSL validation: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const validateZSLDescription = `Validates a complete Zapier System Language (ZSL) object.
This tool performs comprehensive validation checks on the provided ZSL system definition.
Use this tool after constructing or modifying a ZSL configuration, primarily before attempting to update a Zap using the 'update_zap' tool, to catch potential issues.
The tool will return whether the ZSL is valid, and if not, a list of validation errors.

Required parameters:
- The ZSL object itself: A complete ZSL JSON object that defines the system's capabilities, properties, and behaviors.`;
